/**
 * ProgressBar — animated horizontal progress indicator.
 *
 * Fill width driven by Reanimated shared value with timing animation.
 * Respects useReducedMotion().
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §16
 */

import React, { useEffect } from 'react'
import { View, Text } from 'react-native'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  useReducedMotion,
} from 'react-native-reanimated'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '../../lib/theme/tokens'
import { FONTS } from '../../lib/brand'
import { cn } from '../../lib/theme/variants'

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProgressBarProps {
  /** Progress value 0.0 – 1.0 */
  value: number
  /** Visual height (default: 4) */
  height?: number
  /** Track color */
  trackColor?: string
  /** Fill color */
  fillColor?: string
  /** Whether to animate value changes (default: true) */
  animated?: boolean
  /** Animation duration in ms (default: 300) */
  duration?: number
  /** Label displayed above the bar */
  label?: string
  /** Show percentage label on the right */
  showPercentage?: boolean
  /** Border radius (default: full / pill) */
  borderRadius?: number
  /** Additional NativeWind className */
  className?: string
}

// ─── ProgressBar ────────────────────────────────────────────────────────────

export function ProgressBar({
  value,
  height = 4,
  trackColor,
  fillColor,
  animated = true,
  duration = 300,
  label,
  showPercentage = false,
  borderRadius,
  className,
}: ProgressBarProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const progress = useSharedValue(value)
  const clampedValue = Math.min(Math.max(value, 0), 1)

  const resolvedTrackColor = trackColor ?? colors.border
  const resolvedFillColor = fillColor ?? colors.primary
  const resolvedRadius = borderRadius ?? height / 2

  useEffect(() => {
    if (animated && !reducedMotion) {
      progress.value = withTiming(clampedValue, { duration })
    } else {
      progress.value = clampedValue
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- progress is a Reanimated shared value (stable ref)
  }, [clampedValue, animated, duration, reducedMotion])

  const fillStyle = useAnimatedStyle(() => ({
    width: `${progress.value * 100}%`,
  }))

  const percentage = Math.round(clampedValue * 100)

  return (
    <View className={cn(className)}>
      {label || showPercentage ? (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: SPACING.xs,
          }}
        >
          {label ? (
            <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
              {label}
            </Text>
          ) : null}
          {showPercentage && (
            <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textPrimary }}>
              {percentage}%
            </Text>
          )}
        </View>
      ) : null}

      <View
        style={{
          height,
          borderRadius: resolvedRadius,
          backgroundColor: resolvedTrackColor,
          overflow: 'hidden',
        }}
        accessibilityRole="progressbar"
        accessibilityValue={{ min: 0, max: 100, now: percentage }}
      >
        <Animated.View
          style={[
            {
              height,
              borderRadius: resolvedRadius,
              backgroundColor: resolvedFillColor,
            },
            fillStyle,
          ]}
        />
      </View>
    </View>
  )
}
